import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import SafeIcon from '../../common/SafeIcon';
import { TEAM_K9_LOGO_URL, FALLBACK_LOGO_URL } from '../../common/assets';

const CompleteTrainerProfile = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [redirect, setRedirect] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    businessName: '',
    accessCode: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        navigate('/trainer-login');
        return;
      }

      // Check if trainer already has a profile
      const { data: profile } = await supabase
        .from('trainer_profiles')
        .select('*')
        .eq('user_id', authUser.id)
        .single();
      
      if (profile) {
        // Profile exists, redirect to dashboard
        sessionStorage.setItem('trainerAccessVerified', 'true');
        sessionStorage.setItem('trainerProfileId', profile.id);
        setRedirect(true);
        return;
      }

      setUser(authUser);
    };
    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate access code
    if (formData.accessCode.length !== 4) {
      setError('Access code must be exactly 4 digits');
      setLoading(false);
      return;
    }

    // Check if access code is all digits
    if (!/^\d{4}$/.test(formData.accessCode)) {
      setError('Access code must be 4 digits');
      setLoading(false);
      return;
    }

    try {
      // Create trainer profile
      const { data: profile, error: profileError } = await supabase
        .from('trainer_profiles')
        .insert([
          {
            user_id: user.id,
            email: user.email,
            first_name: formData.firstName,
            business_name: formData.businessName,
            access_code: formData.accessCode
          }
        ])
        .select()
        .single();

      if (profileError) throw profileError;

      // Store verification in session and redirect
      sessionStorage.setItem('trainerAccessVerified', 'true');
      sessionStorage.setItem('trainerProfileId', profile.id);
      setRedirect(true);

    } catch (error) {
      setError(error.message || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  if (redirect) {
    navigate('/dashboard');
    return null;
  }

  if (!user) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          {/* Logo/Branding */}
          <div className="text-center mb-8">
            <img 
              src={logoError ? FALLBACK_LOGO_URL : TEAM_K9_LOGO_URL}
              alt="Team K9 Logo" 
              className="w-32 h-32 sm:w-36 sm:h-36 mx-auto mb-6 object-contain"
              onError={() => setLogoError(true)}
            />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Team K9 Training</h1>
            <p className="text-gray-600 text-base sm:text-lg">Complete Your Profile</p>
          </div>

          {/* Profile Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8">
            <div className="mb-6">
              <p className="text-sm text-gray-600">Welcome! You're signed in as:</p>
              <p className="font-medium text-gray-900">{user.email}</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  id="firstName"
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="Enter your first name"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50 focus:bg-white outline-none text-base"
                />
              </div>

              <div>
                <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name
                </label>
                <input
                  id="businessName"
                  type="text"
                  required
                  value={formData.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                  placeholder="Enter your business name"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50 focus:bg-white outline-none text-base"
                />
              </div>

              <div>
                <label htmlFor="accessCode" className="block text-sm font-medium text-gray-700 mb-2">
                  4-Digit Access Code
                </label>
                <input
                  id="accessCode"
                  type="text"
                  required
                  maxLength={4}
                  value={formData.accessCode}
                  onChange={(e) => setFormData({ ...formData, accessCode: e.target.value.replace(/\D/g, '') })}
                  placeholder="0000"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-gray-50 focus:bg-white outline-none text-center text-2xl tracking-widest font-mono"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Choose a 4-digit code for quick access to your dashboard
                </p>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-start gap-2">
                  <SafeIcon name="AlertCircle" className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !formData.firstName || !formData.businessName || formData.accessCode.length !== 4}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3.5 sm:py-4 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Complete Profile</span>
                    <SafeIcon name="CheckCircle" className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteTrainerProfile;